const TOKEN_REGEX = /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g;

const COLORS = {
  key: "#9cdcfe",
  string: "#ce9178",
  number: "#b5cea8",
  keyword: "#569cd6", // true, false, null
};

const escapeHtml = (str: string): string =>
  str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Returns an HTML string with inline-colored spans for JSON tokens, from raw
 * (possibly invalid) JSON text. HTML is escaped, so the input may come from
 * user edits. Intended for use with `dangerouslySetInnerHTML` inside a `<pre>`.
 */
export const syntaxHighlightJsonText = (text: string): string => {
  return escapeHtml(text).replace(TOKEN_REGEX, (match) => {
    let color = COLORS.number;
    if (match.startsWith('"')) {
      color = match.endsWith(":") ? COLORS.key : COLORS.string;
    } else if (/true|false|null/.test(match)) {
      color = COLORS.keyword;
    }
    return `<span style="color:${color}">${match}</span>`;
  });
};

/**
 * Returns an HTML string with inline-colored spans for JSON tokens.
 * Intended for use with `dangerouslySetInnerHTML` inside a `<pre>`.
 */
export const syntaxHighlightJson = (obj: unknown, indent = 4): string => {
  return syntaxHighlightJsonText(JSON.stringify(obj, null, indent) ?? "");
};

/**
 * Human-readable JSON.parse error: V8 only reports "at position N" on older
 * engines, and never shows the offending text. Adds the line/column and the
 * line itself, so a settings file can be fixed without hunting for the offset.
 */
export const formatJsonParseError = (err: unknown, text: string): string => {
  const message = (err as Error)?.message ?? String(err);
  const posMatch = message.match(/at position (\d+)/);
  const lineMatch = message.match(/line (\d+) column (\d+)/);

  let line: number | undefined;
  let column: number | undefined;
  if (lineMatch) {
    line = parseInt(lineMatch[1], 10);
    column = parseInt(lineMatch[2], 10);
  } else if (posMatch) {
    const pos = Math.min(parseInt(posMatch[1], 10), text.length);
    const before = text.slice(0, pos);
    line = before.split("\n").length;
    column = pos - before.lastIndexOf("\n");
  }
  if (line === undefined || column === undefined) return message;

  const source = text.split("\n")[line - 1] ?? "";
  const excerpt = source.length > 120 ? `${source.slice(0, 120)}…` : source;
  const located = /line \d+/.test(message) ? message : `${message} (ligne ${line}, colonne ${column})`;
  return excerpt.trim() ? `${located}\nLigne ${line} : ${excerpt}` : located;
};
